import { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { TranslationField, ProductTranslations } from '../types/translationTypes';
import {
  hashText,
  collectTranslatableFields,
  applyTranslations,
  fetchTranslationsFromBackend,
  getProductTranslation,
  getSellerHashCache,
  saveProductTranslation,
  saveSellerHashCache,
} from '../services/translationCacheService';

interface FormDataForTranslation {
  items: Array<{ id: string; name: string; description?: string | null; productId?: string | null }>;
  paymentTerms?: string | null;
  notes?: string | null;
  offerText?: string | null;
  projectReferenceName?: string | null;
}

const useTranslationReview = () => {
  const { currentUser } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fields, setFields] = useState<TranslationField[]>([]);
  const [onProceedCallback, setOnProceedCallback] = useState<((translatedData: any) => void) | null>(null);
  const [pendingFormData, setPendingFormData] = useState<any>(null);

  /**
   * Check caches, fetch missing translations from the backend, then open the modal.
   */
  const prepareAndOpenModal = useCallback(async (
    formData: FormDataForTranslation,
    targetLang: string,
    isOffer: boolean,
    onProceed: (translatedData: any) => void
  ) => {
    if (!currentUser) {
      onProceed(formData);
      return;
    }

    setIsLoading(true);
    setIsModalOpen(true);
    setPendingFormData(formData);
    // Store callback – wrap in function so useState doesn't call it
    setOnProceedCallback(() => onProceed);

    try {
      const translatableFields = collectTranslatableFields(formData, isOffer);

      if (translatableFields.length === 0) {
        // Nothing to translate – close modal and proceed immediately
        setIsModalOpen(false);
        setIsLoading(false);
        onProceed(formData);
        return;
      }

      const uid = currentUser.uid;

      // ----- Cache Lookup -----
      const sellerCache = await getSellerHashCache(uid, targetLang);
      const resolvedFields: TranslationField[] = [];
      const uncachedFields = [];

      for (const tf of translatableFields) {
        let cachedText: string | null = null;

        if (tf.productId && (tf.fieldType === 'item_name' || tf.fieldType === 'item_description')) {
          // Product cache
          const productCache = await getProductTranslation(tf.productId, targetLang);
          if (productCache) {
            const fieldKey = tf.fieldType === 'item_name' ? 'name' : 'description';
            const hashKey = tf.fieldType === 'item_name' ? 'nameSourceHash' : 'descriptionSourceHash';
            const currentHash = hashText(tf.originalText);
            if (productCache[fieldKey] && productCache[hashKey] === currentHash) {
              cachedText = productCache[fieldKey]!;
            }
          }
        } else {
          // Seller hash cache
          const hash = hashText(tf.originalText);
          if (sellerCache[hash]) {
            cachedText = sellerCache[hash];
          }
        }

        if (cachedText !== null) {
          resolvedFields.push({ ...tf, translatedText: cachedText, isFromCache: true });
        } else {
          uncachedFields.push(tf);
        }
      }

      // ----- Backend Translation for uncached fields -----
      let backendTranslations: Record<string, string> = {};
      if (uncachedFields.length > 0) {
        backendTranslations = await fetchTranslationsFromBackend(uncachedFields, targetLang);
      }

      for (const tf of uncachedFields) {
        resolvedFields.push({
          ...tf,
          translatedText: backendTranslations[tf.key] ?? tf.originalText,
          isFromCache: false,
        });
      }

      // Sort to preserve original field order
      resolvedFields.sort((a, b) => {
        const keyOrder = translatableFields.findIndex(f => f.key === a.key) -
          translatableFields.findIndex(f => f.key === b.key);
        return keyOrder;
      });

      setFields(resolvedFields);
    } catch (err) {
      console.error('[useTranslationReview] Error preparing translations:', err);
      // On error, proceed without translation
      setIsModalOpen(false);
      onProceed(formData);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  /**
   * Called when the user confirms the translations in the modal.
   * Saves new translations to cache, applies them to formData, and calls onProceed.
   */
  const handleConfirm = useCallback(async (
    approvedFields: TranslationField[],
    targetLang: string
  ) => {
    if (!currentUser || !pendingFormData || !onProceedCallback) return;

    const uid = currentUser.uid;

    // ----- Persist to cache (fire-and-forget) -----
    const sellerCacheEntries: Record<string, string> = {};
    const productUpdates: Record<string, ProductTranslations> = {};

    for (const field of approvedFields) {
      if (field.productId && (field.fieldType === 'item_name' || field.fieldType === 'item_description')) {
        if (!productUpdates[field.productId]) productUpdates[field.productId] = {};
        const hash = hashText(field.originalText);
        if (field.fieldType === 'item_name') {
          productUpdates[field.productId].name = field.translatedText;
          productUpdates[field.productId].nameSourceHash = hash;
        } else {
          productUpdates[field.productId].description = field.translatedText;
          productUpdates[field.productId].descriptionSourceHash = hash;
        }
      } else {
        const hash = hashText(field.originalText);
        sellerCacheEntries[hash] = field.translatedText;
      }
    }

    // Save caches (don't await – non-blocking)
    if (Object.keys(sellerCacheEntries).length > 0) {
      saveSellerHashCache(uid, targetLang, sellerCacheEntries).catch(console.warn);
    }
    for (const [productId, data] of Object.entries(productUpdates)) {
      saveProductTranslation(productId, targetLang, data).catch(console.warn);
    }

    // Apply translations to formData
    const translatedData = applyTranslations(pendingFormData, approvedFields);

    setIsModalOpen(false);
    setFields([]);
    setPendingFormData(null);

    onProceedCallback(translatedData);
  }, [currentUser, pendingFormData, onProceedCallback]);

  const handleCancel = useCallback(() => {
    setIsModalOpen(false);
    setFields([]);
    setPendingFormData(null);
    setOnProceedCallback(null);
  }, []);

  const handleFieldChange = useCallback((key: string, text: string) => {
    setFields(prev => prev.map(f => f.key === key ? { ...f, translatedText: text } : f));
  }, []);

  return {
    isModalOpen,
    isLoading,
    fields,
    handleFieldChange,
    prepareAndOpenModal,
    handleConfirm,
    handleCancel,
  };
};

export default useTranslationReview;
